import { useEffect, useMemo, useState } from 'react';

import { Button, ButtonV2, ChevronLeftIcon, ModalV2 as Modal, toast } from '@utilities';
import type { SelectOption } from '@utilities/components/Select';
import type { Question, ScreenerQuestionBank } from '@api/instant_survey/types';

import {
  buildScreenerValueMap,
  DEFAULT_TITLE,
  getInitialScreenerPromptValues,
  getScreenerPromptKeys,
  SCREENER_HIERARCHY_OPTIONS,
  SCREENER_URL_VARIABLE_PROMPT_KEY,
  useScreenerQuestionBanks,
} from './utilities';
import BankView from './components/BankView';
import LibraryView from './components/LibraryView';
import PreviewView from './components/PreviewView';

import styles from './_index.module.scss';

interface CommonModalScreenerQuestionProps {
  onClose: () => void;
}

interface CreateModalScreenerQuestionProps extends CommonModalScreenerQuestionProps {
  initialQuestion?: never;
  mode: 'create';
  onSelectQuestion: (question: Question) => Promise<void>;
  onUpdateQuestion?: never;
}

interface EditModalScreenerQuestionProps extends CommonModalScreenerQuestionProps {
  initialQuestion: Question;
  mode: 'edit';
  onSelectQuestion?: never;
  onUpdateQuestion: (question: Question) => Promise<void>;
}

type ModalScreenerQuestionProps = CreateModalScreenerQuestionProps | EditModalScreenerQuestionProps;

const ModalScreenerQuestion = ({
  initialQuestion,
  mode,
  onClose,
  onSelectQuestion,
  onUpdateQuestion,
}: ModalScreenerQuestionProps) => {
  const isEditing = mode === 'edit';
  const {
    data: banks,
    isError: hasBanksError,
    isLoading: isLoadingBanks,
    refetch: refetchBanks,
  } = useScreenerQuestionBanks();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bank, setBank] = useState<null | ScreenerQuestionBank>(null);
  const [previewQuestion, setPreviewQuestion] = useState<null | Question>(
    isEditing ? initialQuestion : null
  );
  const [promptValues, setPromptValues] = useState<Record<string, string>>({});
  const [selectedHierarchyOption, setSelectedHierarchyOption] = useState<null | SelectOption>(null);
  const [touchedPromptKeys, setTouchedPromptKeys] = useState<Set<string>>(() => new Set());

  const questionPromptKeys = useMemo(
    () => getScreenerPromptKeys(previewQuestion),
    [previewQuestion]
  );
  const hasSavedHierarchy = Boolean(
    previewQuestion?.screenerValueMap &&
    Object.prototype.hasOwnProperty.call(
      previewQuestion.screenerValueMap,
      SCREENER_URL_VARIABLE_PROMPT_KEY
    )
  );
  const hasQuestionHierarchy = questionPromptKeys.includes(SCREENER_URL_VARIABLE_PROMPT_KEY);
  const needsBankHierarchyLookup = isEditing && !hasQuestionHierarchy && !hasSavedHierarchy;
  const hasBankHierarchy = useMemo(() => {
    if (!isEditing || !previewQuestion) return false;

    return banks.some(({ questions }) =>
      questions.some((question) => {
        const bankPromptKeys = getScreenerPromptKeys(question);
        const isSourceQuestion =
          previewQuestion.questionSourceId != null &&
          String(question.id) === String(previewQuestion.questionSourceId);
        const hasMatchingLabel =
          Boolean(previewQuestion.screenerLabel) &&
          question.screenerLabel === previewQuestion.screenerLabel;
        const matchesSourceQuestion =
          previewQuestion.questionSourceId != null ? isSourceQuestion : hasMatchingLabel;

        return (
          matchesSourceQuestion &&
          bankPromptKeys.includes(SCREENER_URL_VARIABLE_PROMPT_KEY) &&
          questionPromptKeys.every((promptKey) => bankPromptKeys.includes(promptKey))
        );
      })
    );
  }, [banks, isEditing, previewQuestion, questionPromptKeys]);
  const isBankMetadataLoading = needsBankHierarchyLookup && isLoadingBanks;
  const isBankMetadataError = needsBankHierarchyLookup && hasBanksError;
  const showHierarchySelect = hasQuestionHierarchy || hasSavedHierarchy || hasBankHierarchy;
  const promptKeys = useMemo(
    () =>
      showHierarchySelect && !questionPromptKeys.includes(SCREENER_URL_VARIABLE_PROMPT_KEY)
        ? [...questionPromptKeys, SCREENER_URL_VARIABLE_PROMPT_KEY]
        : questionPromptKeys,
    [questionPromptKeys, showHierarchySelect]
  );
  const missingPromptKeys = useMemo(
    () => promptKeys.filter((promptKey) => !promptValues[promptKey]?.trim()),
    [promptKeys, promptValues]
  );
  const isSubmitDisabled =
    isSubmitting || isBankMetadataLoading || isBankMetadataError || missingPromptKeys.length > 0;

  const resetPromptState = (question: null | Question, includeHierarchy = false) => {
    const initialPromptValues = getInitialScreenerPromptValues(question);
    if (
      includeHierarchy &&
      !initialPromptValues[SCREENER_URL_VARIABLE_PROMPT_KEY]?.trim() &&
      SCREENER_HIERARCHY_OPTIONS[0]
    ) {
      initialPromptValues[SCREENER_URL_VARIABLE_PROMPT_KEY] =
        `[${SCREENER_HIERARCHY_OPTIONS[0].value}]`;
    }
    const initialHierarchyValue = initialPromptValues[SCREENER_URL_VARIABLE_PROMPT_KEY];
    const nextSelectedHierarchyOption =
      SCREENER_HIERARCHY_OPTIONS.find(
        ({ value }) => value === initialHierarchyValue || `[${value}]` === initialHierarchyValue
      ) || null;

    setPromptValues(initialPromptValues);
    setSelectedHierarchyOption(nextSelectedHierarchyOption);
    setTouchedPromptKeys(new Set());
  };

  useEffect(() => {
    const includesHierarchy = Boolean(
      getScreenerPromptKeys(previewQuestion).includes(SCREENER_URL_VARIABLE_PROMPT_KEY) ||
      (previewQuestion?.screenerValueMap &&
        Object.prototype.hasOwnProperty.call(
          previewQuestion.screenerValueMap,
          SCREENER_URL_VARIABLE_PROMPT_KEY
        ))
    );

    resetPromptState(previewQuestion, includesHierarchy);
  }, [previewQuestion]);

  useEffect(() => {
    if (!showHierarchySelect || selectedHierarchyOption) return;

    const hierarchyValue =
      promptValues[SCREENER_URL_VARIABLE_PROMPT_KEY] ||
      previewQuestion?.screenerValueMap?.[SCREENER_URL_VARIABLE_PROMPT_KEY];
    const nextHierarchyOption =
      SCREENER_HIERARCHY_OPTIONS.find(
        ({ value }) => value === hierarchyValue || `[${value}]` === hierarchyValue
      ) || SCREENER_HIERARCHY_OPTIONS[0];

    if (!nextHierarchyOption) return;

    setSelectedHierarchyOption(nextHierarchyOption);
    setPromptValues((currentPromptValues) => ({
      ...currentPromptValues,
      [SCREENER_URL_VARIABLE_PROMPT_KEY]: `[${nextHierarchyOption.value}]`,
    }));
  }, [previewQuestion, promptValues, selectedHierarchyOption, showHierarchySelect]);

  const handleBack = () => {
    if (previewQuestion) {
      setPreviewQuestion(null);
      return;
    }

    setBank(null);
  };

  const handleSubmit = async () => {
    if (!previewQuestion || isSubmitDisabled) return;

    setIsSubmitting(true);
    try {
      const question = {
        ...previewQuestion,
        screenerValueMap: buildScreenerValueMap(promptKeys, promptValues),
      };

      if (mode === 'edit') {
        await onUpdateQuestion(question);
      } else {
        await onSelectQuestion(question);
      }
    } catch (error) {
      if (!isEditing) throw error;

      console.error('Error updating screener question', error);
      toast.error('Error updating question');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleHierarchyChange = (option: SelectOption) => {
    setSelectedHierarchyOption(option);
    setPromptValues((currentPromptValues) => ({
      ...currentPromptValues,
      [SCREENER_URL_VARIABLE_PROMPT_KEY]: `[${option.value}]`,
    }));
    setTouchedPromptKeys((currentTouchedPromptKeys) =>
      new Set(currentTouchedPromptKeys).add(SCREENER_URL_VARIABLE_PROMPT_KEY)
    );
  };

  const handlePromptChange = (promptKey: string, value: string) => {
    setPromptValues((currentPromptValues) => ({
      ...currentPromptValues,
      [promptKey]: value,
    }));
    setTouchedPromptKeys((currentTouchedPromptKeys) =>
      new Set(currentTouchedPromptKeys).add(promptKey)
    );
  };

  const title =
    previewQuestion?.screenerLabel ||
    (isEditing ? previewQuestion?.text : bank?.name) ||
    DEFAULT_TITLE;
  const isNestedView = Boolean(previewQuestion || bank);

  const actionButtons = previewQuestion
    ? [
        <Button
          isDisabled={isSubmitting}
          key="modal-cancel-button"
          onClick={onClose}
          text="Cancel"
          variant="secondary"
        />,
        <Button
          isDisabled={isSubmitDisabled}
          isLoading={isSubmitting}
          key="modal-confirm-button"
          onClick={handleSubmit}
          text={isEditing ? 'Apply Changes' : 'Add to Survey'}
        />,
      ]
    : [];

  return (
    <Modal
      buttons={actionButtons}
      className={styles['modal-screener-question']}
      isActive
      isScrollable
      onClose={onClose}
      size="fullscreen"
      title={title}
    >
      {isNestedView && !isEditing && (
        <div className={styles['modal-screener-question-view-header']}>
          <button
            aria-label="Back"
            className={styles['modal-screener-question-back']}
            onClick={handleBack}
            type="button"
          >
            <ChevronLeftIcon />
          </button>
        </div>
      )}

      {!bank && !previewQuestion && (
        <LibraryView
          banks={banks}
          isError={hasBanksError}
          isLoading={isLoadingBanks}
          onRetry={refetchBanks}
          onSelectBank={setBank}
        />
      )}
      {bank && !previewQuestion && <BankView bank={bank} onPreviewQuestion={setPreviewQuestion} />}
      {previewQuestion && (
        <>
          {isBankMetadataLoading && (
            <p className={styles['modal-screener-question-status']}>
              Loading screener question details...
            </p>
          )}
          {isBankMetadataError && (
            <p className={styles['modal-screener-question-status']}>
              Screener question details could not be loaded.{' '}
              <ButtonV2 onClick={refetchBanks} size="small" type="button" variant="default">
                Try again.
              </ButtonV2>
            </p>
          )}
          <PreviewView
            isEditing={isEditing}
            missingPromptKeys={missingPromptKeys}
            onHierarchyChange={handleHierarchyChange}
            onPromptChange={handlePromptChange}
            onResetPrompts={() => resetPromptState(previewQuestion, showHierarchySelect)}
            previewQuestion={previewQuestion}
            promptValues={promptValues}
            selectedHierarchyOption={selectedHierarchyOption}
            showHierarchySelect={showHierarchySelect}
            touchedPromptKeys={touchedPromptKeys}
          />
        </>
      )}
    </Modal>
  );
};

export default ModalScreenerQuestion;
